import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { EpisodeService } from '../../services/EpisodeService';
import { Episode } from '../../types/types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'welcome' | 'episodes' | 'recent'>('welcome');
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'date' | 'number' | 'custom'>('date');
  const [selectedEpisodes, setSelectedEpisodes] = useState<string[]>([]);
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);
  const [episodesToDelete, setEpisodesToDelete] = useState<string[]>([]);
  const [reorderMode, setReorderMode] = useState<boolean>(false);
  const [savingOrder, setSavingOrder] = useState<boolean>(false);

  // Form states for new episode
  const [newEpisode, setNewEpisode] = useState<Omit<Episode, 'id' | 'createdAt' | 'updatedAt'>>({
    number: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    duration: '',
    thumbnailId: '',
    videoId: '',
    description: '',
    isRecent: false,
    displayOrder: 0
  });

  // Load episodes on component mount
  useEffect(() => {
    loadEpisodes();
  }, []);

  const loadEpisodes = async () => {
    try {
      setLoading(true);
      const data = await EpisodeService.getAllEpisodes();
      
      // Assign displayOrder if not present
      const dataWithOrder = data.map((episode, index) => ({
        ...episode,
        displayOrder: episode.displayOrder !== undefined ? episode.displayOrder : index
      }));
      
      setEpisodes(dataWithOrder);
    } catch (err) {
      setError('Failed to load episodes');
      console.error('Error loading episodes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewEpisode(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddEpisode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Extract YouTube ID from URL if full URL was provided
      let videoId = newEpisode.videoId;
      if (videoId.includes('youtube.com') || videoId.includes('youtu.be')) {
        const url = new URL(videoId);
        if (videoId.includes('youtube.com')) {
          videoId = url.searchParams.get('v') || '';
        } else {
          videoId = url.pathname.substring(1);
        }
      }
      
      const thumbnailId = newEpisode.thumbnailId || videoId;
      
      const episodeData = {
        ...newEpisode,
        videoId,
        thumbnailId
      };
      
      const result = await EpisodeService.addEpisode(episodeData);
      
      if (result) {
        setEpisodes(prev => [result, ...prev]);
        
        // Reset form
        setNewEpisode({
          number: '',
          title: '',
          date: new Date().toISOString().split('T')[0],
          duration: '',
          thumbnailId: '',
          videoId: '',
          description: '',
          isRecent: false,
          displayOrder: 0
        });
        
        // If marked as recent, update other episodes
        if (result.isRecent && result.id) {
          await EpisodeService.setRecentEpisode(result.id);
        }

        // Switch to manage episodes tab
        setActiveTab('recent');
      }
    } catch (err) {
      setError('Failed to add episode');
      console.error('Error adding episode:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSetRecent = async (id: string) => {
    try {
      setLoading(true);
      const success = await EpisodeService.setRecentEpisode(id);
      
      if (success) {
        // Update local state
        setEpisodes(prev => 
          prev.map(ep => ({
            ...ep,
            isRecent: ep.id === id
          }))
        );
      }
    } catch (err) {
      setError('Failed to set recent episode');
      console.error('Error setting recent episode:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEpisode = async (id: string) => {
    setEpisodesToDelete([id]);
    setDeleteConfirmation(true);
  };

  const handleSelectEpisode = (id: string) => {
    setSelectedEpisodes(prev => {
      if (prev.includes(id)) {
        return prev.filter(epId => epId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedEpisodes.length === episodes.length) {
      // Deselect all
      setSelectedEpisodes([]);
    } else {
      // Select all
      setSelectedEpisodes(episodes.map(ep => ep.id || '').filter(id => id !== ''));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedEpisodes.length === 0) return;
    
    setEpisodesToDelete(selectedEpisodes);
    setDeleteConfirmation(true);
  };

  const confirmDeleteSelected = async () => {
    try {
      setLoading(true);
      const success = await EpisodeService.deleteMultipleEpisodes(episodesToDelete);
      
      if (success) {
        // Remove from local state
        setEpisodes(prev => prev.filter(ep => ep.id && !episodesToDelete.includes(ep.id)));
        setSelectedEpisodes([]);
      }
    } catch (err) {
      setError('Failed to delete episodes');
      console.error('Error deleting episodes:', err);
    } finally {
      setLoading(false);
      setDeleteConfirmation(false);
      setEpisodesToDelete([]);
    }
  };

  const cancelDeleteSelected = () => {
    setDeleteConfirmation(false);
    setEpisodesToDelete([]);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOrder(e.target.value as 'date' | 'number' | 'custom');
  };

  const handleToggleReorderMode = () => {
    setReorderMode(prev => !prev);
  };

  const handleMoveEpisode = async (id: string, direction: 'up' | 'down') => {
    const currentEpisodes = [...episodes];
    const index = currentEpisodes.findIndex(ep => ep.id === id);
    
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === currentEpisodes.length - 1)) {
      return; // Already at the edge
    }
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const episodeToMove = currentEpisodes[index];
    const otherEpisode = currentEpisodes[newIndex];
    
    // Swap the display orders
    const tempOrder = episodeToMove.displayOrder;
    episodeToMove.displayOrder = otherEpisode.displayOrder;
    otherEpisode.displayOrder = tempOrder;
    
    // Swap positions in array
    currentEpisodes[index] = otherEpisode;
    currentEpisodes[newIndex] = episodeToMove;
    
    setEpisodes(currentEpisodes);
  };

  const saveCustomOrder = async () => {
    try {
      setSavingOrder(true);
      
      // First, normalize the display orders to be consecutive integers
      const normalizedEpisodes = [...episodes].map((ep, idx) => ({
        ...ep,
        displayOrder: idx
      }));
      
      setEpisodes(normalizedEpisodes);
      
      // Save each episode's display order to Firestore
      const updatePromises = normalizedEpisodes.map(episode => 
        episode.id && episode.displayOrder !== undefined ? 
          EpisodeService.updateEpisodeOrder(episode.id, episode.displayOrder) : 
          Promise.resolve(false)
      );
      
      await Promise.all(updatePromises);
      
      setReorderMode(false);
    } catch (err) {
      setError('Failed to save custom order');
      console.error('Error saving custom order:', err);
    } finally {
      setSavingOrder(false);
    }
  };

  // Sort episodes based on current sort order
  const sortedEpisodes = [...episodes].sort((a, b) => {
    if (sortOrder === 'date') {
      // Sort by date (newest first)
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    } else if (sortOrder === 'number') {
      // Sort by episode number (try to extract numeric part)
      const aMatch = a.number.match(/\d+/);
      const bMatch = b.number.match(/\d+/);
      
      if (aMatch && bMatch) {
        return parseInt(bMatch[0]) - parseInt(aMatch[0]);
      }
      
      // Fallback to string comparison
      return b.number.localeCompare(a.number);
    } else {
      // Sort by custom display order
      const orderA = a.displayOrder !== undefined ? a.displayOrder : 0;
      const orderB = b.displayOrder !== undefined ? b.displayOrder : 0;
      return orderA - orderB;
    }
  });

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">ADMIN DASHBOARD</h1>
          <button onClick={handleLogout} className="logout-button">
            Sign Out
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="dashboard-tabs">
          <button 
            className={`tab-button ${activeTab === 'welcome' ? 'active' : ''}`}
            onClick={() => setActiveTab('welcome')}
          >
            Welcome
          </button>
          <button 
            className={`tab-button ${activeTab === 'episodes' ? 'active' : ''}`}
            onClick={() => setActiveTab('episodes')}
          >
            Add Episode
          </button>
          <button 
            className={`tab-button ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Manage Episodes
          </button>
        </div>

        {/* Tab Content */}
        <div className="dashboard-content">
          {activeTab === 'welcome' && (
            <>
              <img 
                src={require('../../assets/logo.png')} 
                alt="Basketball Culture Logo" 
                className="dashboard-logo"
              />
              <h2 className="welcome-message">Welcome, {currentUser?.displayName?.split(' ')[0]}!</h2>
              <p className="welcome-subtitle">Basketball Culture Admin Panel</p>
              <p className="welcome-description">
                This is your admin dashboard where you can manage podcast episodes.
                Use the tabs above to add new episodes or set the recent episode that appears on the homepage.
              </p>
            </>
          )}

          {activeTab === 'episodes' && (
            <div className="episodes-form-container">
              <h2 className="form-title">Add New Episode</h2>
              
              {error && <div className="error-message">{error}</div>}
              
              <form onSubmit={handleAddEpisode} className="episode-form">
                <div className="form-group">
                  <label htmlFor="number">Episode Number</label>
                  <input
                    type="text"
                    id="number"
                    name="number"
                    value={newEpisode.number}
                    onChange={handleInputChange}
                    placeholder="e.g. Episode 7"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="title">Episode Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={newEpisode.title}
                    onChange={handleInputChange}
                    placeholder="Enter episode title"
                    required
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="date">Release Date</label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={newEpisode.date}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="duration">Duration</label>
                    <input
                      type="text"
                      id="duration"
                      name="duration"
                      value={newEpisode.duration}
                      onChange={handleInputChange}
                      placeholder="e.g. 1:30:45"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="videoId">YouTube Video ID or URL</label>
                  <input
                    type="text"
                    id="videoId"
                    name="videoId"
                    value={newEpisode.videoId}
                    onChange={handleInputChange}
                    placeholder="e.g. dQw4w9WgXcQ or full YouTube URL"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="thumbnailId">
                    Thumbnail ID (leave empty to use video ID)
                  </label>
                  <input
                    type="text"
                    id="thumbnailId"
                    name="thumbnailId"
                    value={newEpisode.thumbnailId}
                    onChange={handleInputChange}
                    placeholder="Optional: Custom thumbnail ID"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="description">Episode Description</label>
                  <textarea
                    id="description"
                    name="description"
                    value={newEpisode.description}
                    onChange={handleInputChange}
                    placeholder="Enter episode description"
                    rows={5}
                  />
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="isRecent"
                      checked={newEpisode.isRecent}
                      onChange={(e) => setNewEpisode(prev => ({
                        ...prev,
                        isRecent: e.target.checked
                      }))}
                    />
                    Set as recent episode (featured on homepage)
                  </label>
                </div>
                
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Episode'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'recent' && (
            <div className="manage-episodes-container">
              <h2 className="form-title">Manage Episodes</h2>
              
              {error && <div className="error-message">{error}</div>}
              
              {loading ? (
                <div className="loading">Loading episodes...</div>
              ) : episodes.length === 0 ? (
                <div className="no-episodes">No episodes found. Add your first episode!</div>
              ) : (
                <>
                  <div className="episodes-actions">
                    <div className="sort-controls">
                      <label htmlFor="sort-order">Sort by:</label>
                      <select 
                        id="sort-order" 
                        value={sortOrder} 
                        onChange={handleSortChange}
                        className="sort-select"
                        disabled={reorderMode}
                      >
                        <option value="date">Release Date (newest first)</option>
                        <option value="number">Episode Number (newest first)</option>
                        <option value="custom">Custom Order</option>
                      </select>
                      
                      {sortOrder === 'custom' && (
                        <button 
                          className={`reorder-mode-button ${reorderMode ? 'active' : ''}`}
                          onClick={handleToggleReorderMode}
                        >
                          {reorderMode ? 'Exit Reorder Mode' : 'Reorder Episodes'}
                        </button>
                      )}
                      
                      {reorderMode && (
                        <button 
                          className="save-order-button"
                          onClick={saveCustomOrder}
                          disabled={savingOrder}
                        >
                          {savingOrder ? 'Saving...' : 'Save Order'}
                        </button>
                      )}
                    </div>
                    
                    {!reorderMode && (
                      <div className="bulk-actions">
                        <button 
                          className="select-all-button"
                          onClick={handleSelectAll}
                        >
                          {selectedEpisodes.length === episodes.length ? 'Deselect All' : 'Select All'}
                        </button>
                        
                        <button 
                          className="delete-selected-button"
                          onClick={handleDeleteSelected}
                          disabled={selectedEpisodes.length === 0}
                        >
                          Delete Selected ({selectedEpisodes.length})
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {deleteConfirmation && (
                    <div className="delete-confirmation">
                      <p>Are you sure you want to delete {episodesToDelete.length} episode(s)?</p>
                      <div className="confirmation-actions">
                        <button 
                          className="confirm-delete-button"
                          onClick={confirmDeleteSelected}
                        >
                          Yes, Delete
                        </button>
                        <button 
                          className="cancel-delete-button"
                          onClick={cancelDeleteSelected}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="episodes-list">
                    {sortedEpisodes.map(episode => (
                      <div 
                        key={episode.id} 
                        className={`episode-item ${episode.isRecent ? 'is-recent' : ''} ${
                          episode.id && selectedEpisodes.includes(episode.id) ? 'selected' : ''
                        } ${reorderMode ? 'reorder-mode' : ''}`}
                      >
                        {!reorderMode && (
                          <div className="episode-select">
                            <input 
                              type="checkbox" 
                              checked={episode.id ? selectedEpisodes.includes(episode.id) : false}
                              onChange={() => episode.id && handleSelectEpisode(episode.id)}
                            />
                          </div>
                        )}
                        
                        {reorderMode && (
                          <div className="episode-reorder-controls">
                            <button 
                              className="move-up-button"
                              onClick={() => episode.id && handleMoveEpisode(episode.id, 'up')}
                              disabled={sortedEpisodes.indexOf(episode) === 0}
                              title="Move Up"
                            >
                              ↑
                            </button>
                            <button 
                              className="move-down-button"
                              onClick={() => episode.id && handleMoveEpisode(episode.id, 'down')}
                              disabled={sortedEpisodes.indexOf(episode) === sortedEpisodes.length - 1}
                              title="Move Down"
                            >
                              ↓
                            </button>
                          </div>
                        )}
                        
                        <div className="episode-thumbnail">
                          <img 
                            src={`https://img.youtube.com/vi/${episode.thumbnailId}/mqdefault.jpg`}
                            alt={`${episode.title} Thumbnail`}
                          />
                        </div>
                        
                        <div className="episode-details">
                          <h3>{episode.number}: {episode.title}</h3>
                          <p className="episode-meta">
                            <span>{episode.date}</span>
                            <span>{episode.duration}</span>
                            {sortOrder === 'custom' && (
                              <span className="display-order">Order: {episode.displayOrder}</span>
                            )}
                          </p>
                        </div>
                        
                        {!reorderMode && (
                          <div className="episode-actions">
                            {episode.isRecent ? (
                              <span className="recent-badge">Current Recent</span>
                            ) : (
                              <button 
                                className="set-recent-button"
                                onClick={() => episode.id && handleSetRecent(episode.id)}
                                disabled={loading}
                              >
                                Set as Recent
                              </button>
                            )}
                            <button 
                              className="delete-button"
                              onClick={() => episode.id && handleDeleteEpisode(episode.id)}
                              disabled={loading}
                              title="Delete Episode"
                            >
                              <span className="delete-icon">×</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 